import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Users as UsersIcon,
  MagnifyingGlass,
  Shield,
  Flask,
  Eyedropper,
  Eye,
  Plus,
  Pencil,
  Trash
} from "@phosphor-icons/react";

const ROLES = [
  { value: "Admin", label: "Admin", icon: Shield, color: "bg-purple-600" },
  { value: "R&D Manager", label: "R&D Manager", icon: Flask, color: "bg-blue-600" },
  { value: "Chemist", label: "Chemist", icon: Eyedropper, color: "bg-green-600" },
  { value: "QC User", label: "QC User", icon: Eye, color: "bg-orange-500" },
  { value: "Viewer", label: "Viewer", icon: Eye, color: "bg-gray-500" }
];

const DEPARTMENTS = ["R&D", "Quality Control", "Production", "Regulatory", "Administration"];

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employeeId: "",
    name: "",
    email: "",
    department: "",
    role: "Chemist",
    password: ""
  });

  const isAdmin = user?.role === "Admin" || user?.role === "admin";

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openAddModal = () => {
    setEditingUser(null);
    setForm({
      employeeId: "",
      name: "",
      email: "",
      department: "",
      role: "Chemist",
      password: ""
    });
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setForm({
      employeeId: u.employeeId,
      name: u.name,
      email: u.email,
      department: u.department || "",
      role: u.role,
      password: ""
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.role) {
      toast.error("Please fill required fields");
      return;
    }

    if (!editingUser && !form.password) {
      toast.error("Password is required for new users");
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: form.name,
          email: form.email,
          department: form.department,
          role: form.role
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("User updated successfully");
      } else {
        // Create new user
        await axios.post(`${API}/users`, form);
        toast.success("User created successfully");
      }
      
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (userId === user?.id) {
      toast.error("You cannot delete yourself");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === "Active" ? "Inactive" : "Active";
    try {
      await axios.put(`${API}/users/${u.id}`, { status: newStatus });
      toast.success(`User ${newStatus === "Active" ? "activated" : "deactivated"}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getRoleInfo = (role) => {
    // Case-insensitive lookup for role matching
    const normalizedRole = role?.toLowerCase();
    const found = ROLES.find(r => r.value.toLowerCase() === normalizedRole);
    return found || ROLES[4]; // Default to Viewer if not found
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.employeeId?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const userStats = {
    total: users.length,
    active: users.filter(u => u.status === "Active").length,
    admin: users.filter(u => u.role === "Admin").length,
    chemist: users.filter(u => u.role === "Chemist").length
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-pulse text-gray-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500">Manage team members and their access roles</p>
        </div>
        {isAdmin && (
          <Button onClick={openAddModal} className="bg-[#7c5cfc] hover:bg-[#6b4ce0]" data-testid="add-user-btn">
            <Plus size={18} className="mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <UsersIcon size={20} className="text-blue-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{userStats.total}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UsersIcon size={20} className="text-green-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{userStats.active}</p>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield size={20} className="text-purple-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{userStats.admin}</p>
                <p className="text-xs text-gray-500">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Eyedropper size={20} className="text-teal-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{userStats.chemist}</p>
                <p className="text-xs text-gray-500">Chemists</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
            data-testid="search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="role-filter">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map(role => (
              <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-700">
            Users ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <UsersIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-xs font-medium text-gray-500">User</TableHead>
                  <TableHead className="text-xs font-medium text-gray-500">Employee ID</TableHead>
                  <TableHead className="text-xs font-medium text-gray-500">Department</TableHead>
                  <TableHead className="text-xs font-medium text-gray-500">Role</TableHead>
                  <TableHead className="text-xs font-medium text-gray-500">Status</TableHead>
                  {isAdmin && <TableHead className="text-xs font-medium text-gray-500">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => {
                  const roleInfo = getRoleInfo(u.role);
                  const Icon = roleInfo.icon;
                  const isCurrentUser = u.id === user?.id;
                  
                  return (
                    <TableRow key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                            {getInitials(u.name)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{u.employeeId || "-"}</TableCell>
                      <TableCell className="text-sm text-gray-600">{u.department || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${roleInfo.color}`}>
                            <Icon size={14} weight="fill" className="text-white" />
                          </div>
                          <span className="text-sm">{roleInfo.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => !isCurrentUser && isAdmin && toggleStatus(u)}
                          disabled={isCurrentUser || !isAdmin}
                          className={`px-2 py-1 text-xs rounded-full ${
                            u.status === "Active" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-gray-100 text-gray-600"
                          } ${!isCurrentUser && isAdmin ? "cursor-pointer hover:opacity-80" : ""}`}
                        >
                          {u.status || "Active"}
                        </button>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {!isCurrentUser ? (
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEditModal(u)}
                                data-testid={`edit-user-${u.id}`}
                              >
                                <Pencil size={16} className="text-gray-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDelete(u.id)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash size={16} className="text-red-500" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">(You)</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee ID *</Label>
                <Input
                  value={form.employeeId}
                  onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                  placeholder="e.g., EMP001"
                  disabled={!!editingUser}
                  required
                  data-testid="employee-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., John Doe"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="e.g., john@company.com"
                required
                data-testid="email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Set initial password"
                  required={!editingUser}
                  data-testid="password-input"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#7c5cfc] hover:bg-[#6b4ce0]" data-testid="save-user-btn">
                {saving ? "Saving..." : (editingUser ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
